import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiKey } from "../../../config/config";
import MessageCard from "../../../components/AdminCards/MessageCard";
import { FaPlus } from "react-icons/fa6";
import { FaTrashAlt, FaDownload, FaUpload } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getSectionForKey, SECTION_NAMES } from "./translationSections";
import { languageHelper, refreshLanguage } from "../../../config/helperFunctions";

function Language() {
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const [translations, setTranslations] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deletedKeys, setDeletedKeys] = useState([]);
  const [search, setSearch] = useState("");
  const [activeSection, setActiveSection] = useState("All");
  const importInputRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${apiKey}/api/rms/admin/language`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        const langEn = JSON.parse(res.data[0].text_en || "{}");
        const langAr = JSON.parse(res.data[0].text_ar || "{}");
        const combined = Object.keys(langEn).map((key) => ({
          key,
          en: langEn[key],
          ar: langAr[key],
          isSaved: true, // Mark as already saved
        }));
        setTranslations(combined);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "Error occurred!");
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [user?.token]);

  const visibleTranslations = useMemo(() => {
    const searchTerm = search.toLowerCase();
    return translations
      .map((item, index) => ({ ...item, _index: index }))
      .filter((item) => {
        const matchesSection =
          activeSection === "All" ||
          getSectionForKey(item.key) === activeSection;
        const matchesSearch =
          !searchTerm ||
          item.key.toLowerCase().includes(searchTerm) ||
          (item.en || "").toLowerCase().includes(searchTerm) ||
          (item.ar || "").toLowerCase().includes(searchTerm);
        return matchesSection && matchesSearch;
      });
  }, [translations, search, activeSection]);

  const sectionCounts = useMemo(() => {
    const counts = {};
    translations.forEach((item) => {
      const section = getSectionForKey(item.key);
      counts[section] = (counts[section] || 0) + 1;
    });
    return counts;
  }, [translations]);

  const handleAddRow = () => {
    setTranslations((prev) => [
      ...prev,
      { key: "", en: "", ar: "", isSaved: false },
    ]);
  };

  const handleInputChange = (realIndex, field, value) => {
    setTranslations((prev) =>
      prev.map((item, i) => (i === realIndex ? { ...item, [field]: value } : item))
    );
  };

  const handleDeleteRow = (realIndex) => {
    const item = translations[realIndex];
    if (item.isSaved) {
      setDeletedKeys((prev) => [...prev, item.key]);
    }
    setTranslations((prev) => prev.filter((_, i) => i !== realIndex));
  };

  const handleExport = () => {
    const payload = translations
      .filter((t) => t.key.trim() !== "")
      .map(({ key, en, ar }) => ({ key, en, ar }));
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `translations-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        if (!Array.isArray(parsed)) {
          throw new Error("Expected a JSON array of { key, en, ar } objects");
        }
        setTranslations((prev) => {
          const byKey = new Map(prev.map((item) => [item.key, { ...item }]));
          let added = 0;
          let updated = 0;
          parsed.forEach(({ key, en, ar }) => {
            if (!key || !key.trim()) return;
            if (byKey.has(key)) {
              const existing = byKey.get(key);
              existing.en = en ?? existing.en;
              existing.ar = ar ?? existing.ar;
              byKey.set(key, existing);
              updated += 1;
            } else {
              byKey.set(key, { key, en: en || "", ar: ar || "", isSaved: false });
              added += 1;
            }
          });
          toast.success(
            `Imported: ${added} new, ${updated} updated. Click Submit to save.`,
            { position: "bottom-right", autoClose: 5000, theme: "colored" }
          );
          return Array.from(byKey.values());
        });
      } catch (err) {
        toast.error("Invalid JSON file: " + err.message, {
          position: "bottom-right",
          autoClose: 5000,
          theme: "colored",
        });
      } finally {
        e.target.value = "";
      }
    };
    reader.readAsText(file);
  };

  const handleSave = (e) => {
    e.preventDefault();

    const langEn = {};
    const langAr = {};

    translations
      .filter((t) => t.key.trim() !== "" && !deletedKeys.includes(t.key))
      .forEach(({ key, en, ar }) => {
        langEn[key] = en;
        langAr[key] = ar;
      });

    setLoading(true);
    axios
      .put(
        `${apiKey}/api/rms/admin/language/1`,
        {
          text_en: JSON.stringify(langEn),
          text_ar: JSON.stringify(langAr),
        },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      )
      .then(() => {
        // languageHelper() only ever reads its module-level dictionary,
        // which was fetched once at page load -- refresh it here so a save
        // in this tab is reflected immediately, not just on next reload.
        refreshLanguage();
        toast.success("Language updated successfully!", {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
        setDeletedKeys([]);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || "Failed to update language!",
          {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          }
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (pageLoading) {
    return (
      <div className="absolute top-0 left-0 w-full h-full grid place-items-center">
        <img src="/assets/gif/dash2.gif" alt="" />
      </div>
    );
  }

  if (error) {
    return <MessageCard error={error} />;
  }

  return (
    <main>
      <div className="relative my-16 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg bg-white rounded-lg p-4 border-0">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-d-bg-text text-2xl">{languageHelper("Language")}</h1>
        </div>

        <div className="w-full relative">
          <div className="flex flex-wrap justify-between items-center gap-2 mb-4 bg-white w-full sticky top-4 z-10 p-4 shadow rounded">
            <div className="flex flex-wrap gap-2">
              {SECTION_NAMES.map((section) => (
                <button
                  key={section}
                  type="button"
                  onClick={() => setActiveSection(section)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                    activeSection === section
                      ? "bg-d-cat-btn text-white border-d-cat-btn"
                      : "bg-white text-d-bg-text border-gray-300"
                  }`}
                >
                  {section}
                  {section !== "All" && sectionCounts[section] ? (
                    <span className="ml-1 opacity-70">
                      ({sectionCounts[section]})
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-64"
                placeholder={languageHelper("Search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button
                type="button"
                title="Export JSON"
                onClick={handleExport}
                className="flex items-center gap-1 bg-d-cat-btn text-white text-xs px-3 py-2 rounded shadow hover:shadow-lg"
              >
                <FaDownload /> {languageHelper("Download Sample")}
              </button>
              <button
                type="button"
                title="Import JSON"
                onClick={handleImportClick}
                className="flex items-center gap-1 bg-d-cat-btn text-white text-xs px-3 py-2 rounded shadow hover:shadow-lg"
              >
                <FaUpload /> {languageHelper("Upload File")}
              </button>
              <input
                ref={importInputRef}
                type="file"
                accept="application/json"
                hidden
                onChange={handleImportFile}
              />
            </div>
          </div>
          <form onSubmit={handleSave} className="w-full">
            <div className="grid grid-cols-[1fr_1fr_1fr_auto] text-xl font-medium text-d-bg-text gap-4">
              <h3>{languageHelper("Key")}</h3>
              <h3>{languageHelper("English")}</h3>
              <h3>{languageHelper("Arabic")}</h3>
              <h3 aria-hidden="true">&nbsp;</h3>
            </div>
            <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-4 mt-4 items-center">
              {visibleTranslations.map((item) => (
                <React.Fragment key={item._index}>
                  <input
                    type="text"
                    className={`px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full ${
                      item.isSaved ? "bg-gray-100" : "bg-white"
                    }`}
                    placeholder={languageHelper("Key")}
                    value={item.key}
                    onChange={(e) =>
                      handleInputChange(item._index, "key", e.target.value)
                    }
                    disabled={item.isSaved}
                  />
                  <input
                    type="text"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    placeholder={languageHelper("English")}
                    value={item.en}
                    onChange={(e) =>
                      handleInputChange(item._index, "en", e.target.value)
                    }
                  />
                  <input
                    type="text"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    placeholder={languageHelper("Arabic")}
                    value={item.ar}
                    onChange={(e) =>
                      handleInputChange(item._index, "ar", e.target.value)
                    }
                  />
                  <button
                    type="button"
                    title="Delete"
                    onClick={() => handleDeleteRow(item._index)}
                    className="text-red-500 hover:text-red-700 px-2 py-2"
                  >
                    <FaTrashAlt />
                  </button>
                </React.Fragment>
              ))}
              {visibleTranslations.length === 0 && (
                <p className="col-span-4 text-center text-gray-400 py-4">
                  {languageHelper("No translations in this section yet.")}
                </p>
              )}
            </div>
            <div className="flex justify-end items-center mt-4">
              <button
                type="button"
                onClick={handleAddRow}
                className="bg-d-cat-btn text-white text-sm px-3 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 duration-150 w-fit"
              >
                <FaPlus />
              </button>
            </div>
            <span className="w-full bg-d-bg h-1 my-2 inline-block"></span>
            <div className="flex gap-2">
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit relative"
                type="submit"
              >
                {loading && (
                  <span className="absolute top-0 left-0 w-full h-full py-1 px-3 bg-black bg-opacity-50">
                    <img
                      className="w-4 mx-auto"
                      src="/assets/gif/button_loader.gif"
                      alt=""
                    />
                  </span>
                )}
                {languageHelper("Submit")}
              </button>
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit"
                type="button"
                onClick={() => navigate(-1)}
              >
                {languageHelper("Back")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}

export default Language;
